import React, { useState, useEffect } from "react";
import Dropdown from "../../components/ui/dropdown";
import Checkbox from "../../components/ui/checkbox";
import PageTitle from "../../components/screens/page-title";
import styles from "./alarms.module.css";
import Table from "../../components/ui/table";
import { useNavigate } from "react-router-dom";
import { getAlarmsSummaryByState, getAlarmsCountByState, getActiveUsers } from "../../services/apiService";
import usePageTitle from "../../hooks/usePageTitle";

// Función para formatear el display de alarma
const formatAlarmDisplay = (days) => {
  if (days === null || days === undefined || days === '') {
    return '-';
  }
  
  const numDays = parseInt(days);
  if (isNaN(numDays)) {
    return '-';
  }
  
  if (numDays < 0) {
    return `faltan ${Math.abs(numDays)} días`;
  }
  
  if (numDays === 0) {
    return 'hoy';
  }
  
  return `hace ${numDays} ${numDays === 1 ? 'día' : 'días'}`;
};

// Función para formatear los datos de alarma
const formatAlarmsData = (alarmsData) => {
  return alarmsData.map(alarm => ({
    ...alarm,
    diasDesdeAlarma: formatAlarmDisplay(alarm.diasDesdeAlarma)
  }));
};

const AlarmsPage = () => {
  usePageTitle('Alarmas');
  const navigate = useNavigate();
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [selectedStates, setSelectedStates] = useState([]);
  const [alarmsData, setAlarmsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterOptions, setFilterOptions] = useState([]);
  const [statesWithCount, setStatesWithCount] = useState({});

  // Mapeo de estados a nombres legibles
  const estadosAlarma = [
    { codigo: "PTE_ASIGNAR_VISITA", nombre: "Pte Asignar Visita" },
    { codigo: "PTE_PARTE", nombre: "Pte Parte" },
    { codigo: "PTE_PRESUPUESTO", nombre: "Pte Presupuesto" },
    { codigo: "PTE_ASIGNAR_TRABAJO", nombre: "Pte Asignar Trabajo" },
    { codigo: "PTE_COMPAÑIA", nombre: "Pte Compañía" },
    { codigo: "PRE_FACTURADO", nombre: "Pre Facturado" },
    { codigo: "PTE_FACTURACION", nombre: "Pte Facturación" },
    { codigo: "FACTURADO", nombre: "Facturado" },
    { codigo: "PTE_ACEPTACION", nombre: "Pte Aceptación" },
    { codigo: "PTE_RECORDATORIO", nombre: "Pte Recordatorio" },
    { codigo: "ASIGNADO", nombre: "Asignado" },
  ];

  const headers = [
    { label: "Nº expediente", key: "expedienteId" },
    { label: "Domicilio", key: "domicilioCliente" },
    { label: "Compañía", key: "companiaNombre" },
    { label: "Tipo", key: "companiaTipo" },
    { label: "Estado", key: "estadoDisplayName" },
    { label: "Alarma", key: "diasDesdeAlarma" },
  ];

  const textColorConditions = {
    diasDesdeAlarma: [
      {
        // Más de 5 días: rojo
        test: (value) => {
          const numValue = parseInt(value);
          return !isNaN(numValue) && numValue > 5;
        },
        color: "var(--red)",
      },
      {
        // Entre 2 y 5 días: amarillo
        test: (value) => {
          const numValue = parseInt(value);
          return !isNaN(numValue) && numValue >= 2 && numValue <= 5;
        },
        color: "var(--progress)",
      },
      {
        // Entre 0 y 2 días: verde
        test: (value) => {
          const numValue = parseInt(value);
          return !isNaN(numValue) && numValue < 2 && numValue >= 0;
        },
        color: "var(--green)",
      },
    ],
  };

  // Cargar opciones de filtrado al montar el componente
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const activeUsers = await getActiveUsers();
        const currentUserId = localStorage.getItem("usuarioId");
        const userRole = localStorage.getItem("userRole");
        
        const userOptions = activeUsers.map(user => ({
          value: user.usuarioId.toString(),
          label: user.nombre,
          type: "user"
        }));
        
        let options;
        
        // Si el usuario tiene rol USER, solo mostrar su usuario, Grupo A y Grupo B
        if (userRole === "ROLE_USER") {
          options = [
            { value: "GRUPO_A", label: "Grupo A", type: "group" },
            { value: "GRUPO_B", label: "Grupo B", type: "group" }
          ];
          
          // Agregar el usuario actual a las opciones
          if (currentUserId) {
            const currentUserOption = userOptions.find(
              user => user.value === currentUserId.toString()
            );
            if (currentUserOption) {
              options.unshift(currentUserOption);
            }
          }
        } else {
          // Para otros roles, mostrar todas las opciones
          options = [
            { value: null, label: "Todos", type: "all" },
            { value: "GRUPO_A", label: "Grupo A", type: "group" },
            { value: "GRUPO_B", label: "Grupo B", type: "group" },
            ...userOptions
          ];
        }
        
        setFilterOptions(options);
        
        // Seleccionar automáticamente el usuario actual
        if (currentUserId) {
          const currentUserOption = userOptions.find(
            user => user.value === currentUserId.toString()
          );
          if (currentUserOption) {
            setSelectedFilter(currentUserOption);
          } else if (options.length > 0) {
            // Si no se encuentra el usuario, seleccionar la primera opción disponible
            setSelectedFilter(options[0]);
          }
        } else if (options.length > 0) {
          // Si no hay usuario, seleccionar la primera opción disponible
          setSelectedFilter(options[0]);
        }
      } catch (error) {
        console.error("Error loading filter options:", error);
        const userRole = localStorage.getItem("userRole");
        const defaultOptions = userRole === "ROLE_USER" 
          ? [
              { value: "GRUPO_A", label: "Grupo A", type: "group" },
              { value: "GRUPO_B", label: "Grupo B", type: "group" },
            ]
          : [
              { value: null, label: "Todos", type: "all" },
              { value: "GRUPO_A", label: "Grupo A", type: "group" },
              { value: "GRUPO_B", label: "Grupo B", type: "group" },
            ];
        setFilterOptions(defaultOptions);
        setSelectedFilter(defaultOptions[0]);
      }
    };

    loadFilterOptions();
  }, []);

  // Función para manejar cambios en el dropdown de filtrado
  const handleFilterChange = (selected) => {
    setSelectedFilter(selected);
  };

  // Función para manejar selección/deselección de estados
  const handleStateToggle = async (estadoCodigo) => {
    const newSelectedStates = selectedStates.includes(estadoCodigo)
      ? selectedStates.filter(state => state !== estadoCodigo)
      : [...selectedStates, estadoCodigo];
    
    setSelectedStates(newSelectedStates);
    
    // Cargar datos filtrados usando la API
    await loadFilteredAlarms(newSelectedStates);
  };

  // Función para cargar alarmas filtradas
  const loadFilteredAlarms = async (states) => {
    try {
      setLoading(true);
      
      // Determinar los parámetros de filtrado
      let usuarioId = "";
      let grupoTrabajo = "";
      
      if (selectedFilter) {
        if (selectedFilter.value === "GRUPO_A") {
          grupoTrabajo = "GRUPO_A";
        } else if (selectedFilter.value === "GRUPO_B") {
          grupoTrabajo = "GRUPO_B";
        } else if (selectedFilter.type === "user") {
          usuarioId = selectedFilter.value;
        }
        // Si selectedFilter.value es null (opción "Todos"), los parámetros quedan vacíos
      }
      
      if (states.length === 0) {
        // Si no hay estados seleccionados, cargar TODAS las alarmas
        const allAlarms = await getAlarmsSummaryByState("", usuarioId, grupoTrabajo);
        const formattedAlarms = Array.isArray(allAlarms) ? formatAlarmsData(allAlarms) : [];
        setAlarmsData(formattedAlarms);
      } else {
        // Si hay filtros, cargar alarmas para cada estado seleccionado
        const allFilteredAlarms = [];
        for (const estadoCodigo of states) {
          const stateAlarms = await getAlarmsSummaryByState(estadoCodigo, usuarioId, grupoTrabajo);
          
          if (Array.isArray(stateAlarms)) {
            allFilteredAlarms.push(...stateAlarms);
          }
        }
        
        const formattedAlarms = formatAlarmsData(allFilteredAlarms);
        setAlarmsData(formattedAlarms);
      }

      // Actualizar conteos de estados
      const statesCountData = await getAlarmsCountByState(usuarioId, grupoTrabajo);
      setStatesWithCount(statesCountData || {});
      
    } catch (error) {
      console.error("Error loading filtered alarms:", error);
      setAlarmsData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (row, e) => {
    try {
      const isMiddle = !!(e && (e.nativeEvent?.button === 1 || e.button === 1 || e.which === 2));
      const isModifier = !!(e && (e.ctrlKey || e.metaKey));
      const url = `/record/${row.expedienteId}`;
      if (isMiddle || isModifier) {
        window.open(url, "_blank");
      } else {
        navigate(url);
      }
    } catch (err) {
      navigate(`/record/${row.expedienteId}`);
    }
  };

  // Recargar datos cuando cambie el filtro
  useEffect(() => {
    loadFilteredAlarms(selectedStates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      <PageTitle iconName="alarmFilled" title="Alarmas" />
      <div className={styles.searchContainer}>
        <Dropdown
          options={filterOptions}
          onSelect={handleFilterChange}
          placeholder="Seleccionar filtro"
          selected={selectedFilter}
          clearable={true}
        />
      </div>
      
      <div className={styles.contentWrapper}>
        <div className={styles.sidebar}>
          <h2 className={styles.sidebarTitle}>Estado del Expediente</h2>
          <div className={styles.statesList}>
            {estadosAlarma.map((estado) => {
              const count = statesWithCount[estado.codigo] || 0;
              return (
                <div
                  key={estado.codigo}
                  className={`${styles.stateItem} ${
                    selectedStates.includes(estado.codigo) ? styles.selected : ""
                  }`}
                  onClick={() => handleStateToggle(estado.codigo)}
                  role="button"
                  tabIndex={0}
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Checkbox
                      checked={selectedStates.includes(estado.codigo)}
                      onChange={() => handleStateToggle(estado.codigo)}
                      style={{ marginRight: "10px" }}
                    />
                    <span className={styles.stateLabel}>
                      {estado.nombre}
                    </span>
                  </div>
                  <span className={styles.stateCount}>
                    {count}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
        
        <div className={styles.tableContainer}>
          {loading ? (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '200px',
              color: 'var(--dark-grey)'
            }}>
              Cargando...
            </div>
          ) : (
            <Table
              headers={headers}
              data={alarmsData}
              onPress={handleRowClick}
              textColorConditions={textColorConditions}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AlarmsPage;